// src/modals/tapSelectionModal.jsx
// Modal for selecting location, sublocation, and tap when moving a keg to "On Tap"

import React, { useState } from 'react';
import { AlertCircle, AlertTriangle } from 'lucide-react';

export const TapSelectionModal = ({
  show,
  onClose,
  keg,
  locations,
  kegs,
  onSave,
  selectedLocation,
  setSelectedLocation,
  selectedSublocation,
  setSelectedSublocation,
  selectedTap,
  setSelectedTap,
  onMoveBeer
}) => {
  const [moveTargetTap, setMoveTargetTap] = useState('');
  if (!show || !keg) return null;

  // Get sublocations for selected location
  const currentLocation = locations.find(loc => loc.name === selectedLocation);
  const sublocations = currentLocation?.sublocations || [];

  // Get taps for selected location/sublocation
  const getTaps = () => {
    if (selectedSublocation && sublocations.length > 0) {
      const sub = sublocations.find(s => s.name === selectedSublocation);
      return sub && sub.hasTaps ? (sub.tapNames || []) : [];
    }
    return currentLocation?.hasTaps ? (currentLocation?.tapNames || []) : [];
  };

  // Find if a tap has a beer already assigned
  const getTapOccupant = (tapName, location, sublocation) => {
    return kegs?.find(k => 
      k.tapStatus === 'On Tap' &&
      k.tapNumber === tapName &&
      k.location === location &&
      k.sublocation === sublocation &&
      k.id !== keg.id  // Don't count the current keg
    );
  };

  // Get empty taps (excluding the current location/sublocation's selected tap)
  const getEmptyTaps = () => {
    if (!selectedLocation) return [];
    
    return availableTaps.filter(tapName => {
      const occupant = getTapOccupant(tapName, selectedLocation, selectedSublocation);
      return !occupant && tapName !== selectedTap;
    });
  };

  const availableTaps = getTaps();
  const hasTaps = availableTaps.length > 0;
  const selectedTapOccupant = selectedTap && selectedLocation 
    ? getTapOccupant(selectedTap, selectedLocation, selectedSublocation)
    : null;

  // Reset sublocation when location changes
  const handleLocationChange = (newLocation) => {
    setSelectedLocation(newLocation);
    setSelectedSublocation('');
    setSelectedTap('');
  };

  // Reset tap when sublocation changes
  const handleSublocationChange = (newSublocation) => {
    setSelectedSublocation(newSublocation);
    setSelectedTap('');
  };

  const handleSave = () => {
    if (!selectedLocation) {
      alert('Please select a location');
      return;
    }
    if (!selectedTap) {
      alert('Please select or enter a tap');
      return;
    }
    // If a tap is occupied, the handler will trigger the kick flow
    // Just pass the selection to the parent
    onSave(selectedLocation, selectedSublocation, selectedTap);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
        <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">
          Assign {keg.name} to a Tap
        </h3>

        <div className="space-y-4">
          {/* Location Selection */}
          <div>
            <label className="block mb-2 font-medium text-gray-900 dark:text-gray-100">Location*</label>
            <select
              value={selectedLocation}
              onChange={(e) => handleLocationChange(e.target.value)}
              className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            >
              <option value="">Select location</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.name}>{loc.name}</option>
              ))}
            </select>
          </div>

          {/* Sublocation Selection - only show if location has sublocations */}
          {selectedLocation && sublocations.length > 0 && (
            <div>
              <label className="block mb-2 font-medium text-gray-900 dark:text-gray-100">Sublocation</label>
              <select
                value={selectedSublocation}
                onChange={(e) => handleSublocationChange(e.target.value)}
                className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              >
                <option value="">Select sublocation</option>
                {sublocations.map(sub => (
                  <option key={sub.id} value={sub.name}>{sub.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Tap Selection/Input */}
          {selectedLocation && (
            <div>
              <label className="block mb-2 font-medium text-gray-900 dark:text-gray-100">Tap*</label>
              {hasTaps ? (
                <div className="space-y-2">
                  <select
                    value={selectedTap}
                    onChange={(e) => setSelectedTap(e.target.value)}
                    className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                  >
                    <option value="">Select tap</option>
                    {availableTaps.map((tapName, idx) => {
                      const occupant = getTapOccupant(tapName, selectedLocation, selectedSublocation);
                      return (
                        <option key={`${tapName}-${idx}`} value={tapName}>
                          {tapName}
                          {occupant ? ` (${occupant.currentBeer?.name || 'Unknown Beer'})` : ' (empty)'}
                        </option>
                      );
                    })}
                  </select>
                  
                  {/* Show occupant info if tap is occupied */}
                  {selectedTapOccupant && (
                    <div className="bg-orange-50 dark:bg-orange-900 dark:bg-opacity-20 border border-orange-200 dark:border-orange-700 rounded p-3 space-y-3">
                      <div className="flex gap-2">
                        <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-500 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-orange-800 dark:text-orange-200">
                          <p className="font-medium">Tap occupied by: {selectedTapOccupant.currentBeer?.name || 'Unknown Beer'}</p>
                          <p className="text-xs">Keg #{selectedTapOccupant.kegNumber} - {selectedTapOccupant.currentBeer?.style}</p>
                        </div>
                      </div>
                      
                      {/* Option to move current beer */}
                      {getEmptyTaps().length > 0 && (
                        <div className="space-y-2 pt-2 border-t border-orange-200 dark:border-orange-700">
                          <p className="text-xs font-medium text-orange-800 dark:text-orange-200">Move this beer to:</p>
                          <div className="flex gap-2">
                            <select
                              value={moveTargetTap}
                              onChange={(e) => setMoveTargetTap(e.target.value)}
                              className="flex-1 border rounded px-2 py-1 text-xs focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                            >
                              <option value="">Select tap...</option>
                              {getEmptyTaps().map((tapName, idx) => (
                                <option key={`${tapName}-${idx}`} value={tapName}>
                                  {tapName}
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={() => {
                                if (moveTargetTap) {
                                  onMoveBeer(selectedTapOccupant, moveTargetTap);
                                  setMoveTargetTap('');
                                }
                              }}
                              disabled={!moveTargetTap}
                              className="px-3 py-1 text-xs bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 text-white rounded transition font-medium"
                            >
                              Move
                            </button>
                          </div>
                        </div>
                      )}
                      
                      <div className="text-xs text-orange-700 dark:text-orange-300 pt-2 border-t border-orange-200 dark:border-orange-700">
                        Or proceed to kick: Click "Assign to Tap" below
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={selectedTap}
                    onChange={(e) => setSelectedTap(e.target.value)}
                    placeholder="e.g., Tap 1, West Wall, etc."
                    className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                  />
                  <div className="bg-yellow-50 dark:bg-yellow-900 dark:bg-opacity-20 border border-yellow-200 dark:border-yellow-700 rounded p-3 flex gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-yellow-800 dark:text-yellow-200">
                      <p className="font-medium mb-1">No taps configured for this location</p>
                      <p className="mb-2">You can enter a custom tap name here, but consider setting up taps in Settings.</p>
                      <button
                        onClick={() => {
                          onClose();
                          // Will navigate to settings - handled by parent
                          window.dispatchEvent(new CustomEvent('navigate-to-settings'));
                        }}
                        className="text-yellow-700 dark:text-yellow-300 underline hover:no-underline font-medium"
                      >
                        Configure taps in Settings →
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-6">
          <button
            onClick={handleSave}
            disabled={!selectedLocation || !selectedTap}
            className="flex-1 bg-green-600 text-white py-2 rounded hover:bg-green-700 transition disabled:bg-gray-300 dark:disabled:bg-gray-600 font-medium"
          >
            Assign to Tap
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-300 dark:bg-gray-600 dark:text-gray-100 py-2 rounded hover:bg-gray-400 dark:hover:bg-gray-500 transition font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
